import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useAuthStore } from '../store/authStore';
import '../styles/learning-journey.css';

const CATEGORY_META: Record<string,{icon:string;color:string;label:string}> = {
  python:     {icon:'🐍',color:'#6366F1',label:'Python Adası'},
  web:        {icon:'🌐',color:'#22D3EE',label:'Web Dünyası'},
  matematik:  {icon:'🧮',color:'#F59E0B',label:'Matematik Diyarı'},
  fen:        {icon:'🔬',color:'#10B981',label:'Bilim Adası'},
  ingilizce:  {icon:'🇬🇧',color:'#EC4899',label:'İngilizce Dünyası'},
  turkce:     {icon:'📖',color:'#8B5CF6',label:'Türkçe Vadisi'},
  yapay_zeka: {icon:'🤖',color:'#F97316',label:'AI Galaksisi'},
  game:       {icon:'🎮',color:'#EF4444',label:'Oyun Adası'},
  veri_analizi: {icon:'📊',color:'#3B82F6',label:'Veri Analizi Adası'},
};

interface CourseMap {id:string;title:string;category:string;difficulty:string;progress:number;isUnlocked:boolean;}

export function LearningJourneyPage() {
  const navigate = useNavigate();
  const {profile} = useAuthStore();
  const [courses, setCourses] = useState<CourseMap[]>([]);
  const [selected, setSelected] = useState<string|null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!profile) return;
    (async () => {
      const {data:courseData} = await supabase.from('courses').select('id,title,category,difficulty').eq('is_published',true).order('order_index');
      const {data:progressData} = await supabase.from('student_course_progress').select('course_id,progress_percent').eq('student_id',profile.id);
      const pm = new Map((progressData??[]).map(p=>[p.course_id,p.progress_percent]));
      const arr = (courseData??[]) as any[];
      setCourses(arr.map((c,i)=>{
        const cat = (c.category || 'other').toLowerCase();
        const normCat = cat === 'science' ? 'fen' :
                        cat === 'math' ? 'matematik' :
                        cat === 'turkish' ? 'turkce' :
                        cat === 'english' ? 'ingilizce' :
                        cat === 'zeka' ? 'veri_analizi' : cat;
        return {
          id:c.id, title:c.title, category:normCat, difficulty:c.difficulty,
          progress:pm.get(c.id)??0,
          isUnlocked: i===0 || (pm.get(arr[i-1]?.id??'')??0)>=30,
        };
      }));
      setIsLoading(false);
    })();
  }, [profile?.id]);

  const grouped = courses.reduce((acc,c)=>{
    const cat=c.category;
    if(!acc[cat]) acc[cat]=[];
    acc[cat].push(c);
    return acc;
  },{} as Record<string,CourseMap[]>);

  if (isLoading) return <div className="courses-loading">🗺️ Harita yükleniyor...</div>;

  return (
    <div className="journey-page">
      <header className="journey-header">
        <h1>🗺️ Öğrenme Haritası</h1>
        <p>Adaları keşfet, seviyeleri tamamla, coin kazan!</p>
      </header>
      <div className="journey-map">
        {Object.entries(grouped).map(([cat,items])=>{
          const meta = CATEGORY_META[cat]||{icon:'📚',color:'#6366F1',label:cat};
          const done = items.filter(i=>i.progress>=100).length;
          return (
            <div key={cat} className={`journey-island ${selected===cat?'selected':''}`}
              style={{'--island-color':meta.color} as any}
              onClick={()=>setSelected(selected===cat?null:cat)}>
              <div className="journey-island-header">
                <span className="journey-island-icon">{meta.icon}</span>
                <div>
                  <h3>{meta.label}</h3>
                  <div className="journey-island-sub">{done}/{items.length} tamamlandı</div>
                </div>
                <div className="journey-island-badge" style={{background:meta.color}}>
                  {Math.round((done/items.length)*100)}%
                </div>
              </div>
              <div className="journey-progress-bar">
                <div className="journey-progress-fill" style={{width:`${(done/items.length)*100}%`,background:meta.color}}/>
              </div>
              {selected===cat && (
                <div className="journey-levels">
                  {items.map((item,idx)=>(
                    <div key={item.id}
                      className={`journey-level-node ${item.isUnlocked?'unlocked':'locked'} ${item.progress>=100?'completed':''}`}
                      style={item.progress>=100 ? { background: 'rgba(16, 185, 129, 0.08)', borderLeft: '3px solid #10b981' } : undefined}
                      onClick={e=>{e.stopPropagation();if(item.isUnlocked)navigate(`/courses/${item.id}`);}}>
                      <div className="journey-level-circle" style={item.progress>=100?{background:'#10b981'}:item.isUnlocked?{background:meta.color}:{}}>
                        {item.progress>=100?'✓':item.isUnlocked?idx+1:'🔒'}
                      </div>
                      <div className="journey-level-info">
                        <span className="journey-level-title" style={item.progress>=100?{color:'#10b981'}:undefined}>{item.title}</span>
                        {item.isUnlocked&&item.progress>0&&(
                          <div className="journey-mini-bar">
                            <div style={{width:`${item.progress}%`,background:item.progress>=100?'#10b981':meta.color}}/>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="card journey-legend">
        <span>🔓 Açık</span><span>🔒 Kilitli (öncekini tamamla)</span><span>✓ Tamamlandı</span>
      </div>
    </div>
  );
}
